import { z } from 'zod';

import type { Language } from '@shared/hooks/locale';

/**
 * Zod schema for `activity.extra_data` on `activity_type = 'lead_status_agent'`
 * rows.
 *
 * Written by the `log_lead_status_agent` Postgres trigger
 * (hasura-crm/migrations/crm/*_lead_status_agent_activity_type). The trigger
 * snapshots the from/to `lead_stages` rows so the feed renders the headline
 * without an extra query. `lead_stages.label` is a Hasura computed field (not a
 * real column), so the per-language `label_*` columns are snapshotted under
 * `labels` and resolved here by the viewer's locale.
 *
 * `labels` is an open record (not a fixed key list): the trigger aggregates
 * whatever `label_*` columns exist, so a newly added language flows through
 * without changes here.
 */
const StageLabelsSchema = z.record(z.string().nullable());

const StageSnapshotSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  labels: StageLabelsSchema.nullable().optional(),
});
export type StageSnapshot = z.infer<typeof StageSnapshotSchema>;

const LeadStatusAgentExtraDataSchema = z.object({
  stage: z.object({
    from: StageSnapshotSchema.nullable(),
    to: StageSnapshotSchema.nullable(),
  }),
  agent_id: z.string().nullable().optional(),
});
export type LeadStatusAgentExtraData = z.infer<
  typeof LeadStatusAgentExtraDataSchema
>;

// WeakMap keyed on the raw extra_data object: Apollo holds stable references
// per row, so subsequent renders hit the cache. Mirrors parseChangeExtraData.
const parseCache = new WeakMap<object, LeadStatusAgentExtraData | null>();
export const parseLeadStatusAgentExtraData = (
  raw: unknown,
): LeadStatusAgentExtraData | null => {
  if (typeof raw !== 'object' || raw == null) {
    return null;
  }
  const cached = parseCache.get(raw);
  if (cached !== undefined) {
    return cached;
  }
  const result = LeadStatusAgentExtraDataSchema.safeParse(raw);
  const parsed = result.success ? result.data : null;
  parseCache.set(raw, parsed);
  return parsed;
};

/**
 * Resolve a stage's display label for the current locale. Falls back to the
 * English label, then the stage `name`, so a stage missing a translation still
 * renders something meaningful instead of collapsing to an em-dash.
 */
export const resolveStageLabel = (
  stage: StageSnapshot | null | undefined,
  language: Language,
): string | null => {
  if (stage == null) {
    return null;
  }
  const label = stage.labels?.[language] || stage.labels?.en || stage.name;
  return label != null && label !== '' ? label : null;
};
